import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Routines.css";

const FREQUENCY_OPTIONS = [
  { value: "minutes", label: "Every X minutes" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DAY_OPTIONS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const dayLabel = (day) => day.slice(0, 3).replace(/^./, (letter) => letter.toUpperCase());

const INTERVAL_PRESETS = [
  { value: 5, label: "5 min" },
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 360, label: "6 hours" },
];

function scheduleLabel(routine) {
  const freq = routine.frequency || "daily";
  const time = routine.executionTime || "09:00";
  const days = routine.executionDays || [];
  const dom = routine.executionDayOfMonth || 1;
  const interval = routine.intervalMinutes || 1440;
  const everyDays = routine.repeatEveryDays || 1;
  const everyWeeks = routine.repeatEveryWeeks || 1;
  const everyMonths = routine.repeatEveryMonths || 1;

  switch (freq) {
    case "minutes":
      return `Every ${interval} min`;
    case "hourly":
      return `Every ${interval >= 60 ? interval / 60 : 1} hour(s)`;
    case "daily":
      return everyDays === 1 ? `Daily at ${time}` : `Every ${everyDays} days at ${time}`;
    case "weekly": {
      const dayLabels = days.length > 0
        ? days.map(dayLabel).join(", ")
        : "Mon";
      const weekLabel = everyWeeks === 1 ? "Every week" : `Every ${everyWeeks} weeks`;
      return `${weekLabel} — ${dayLabels} at ${time}`;
    }
    case "monthly":
      return everyMonths === 1
        ? `Monthly on day ${dom} at ${time}`
        : `Every ${everyMonths} months on day ${dom} at ${time}`;
    default:
      return freq;
  }
}

function Routines() {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [skills, setSkills] = useState([]);
  const [runStatus, setRunStatus] = useState(null);   // { id, type: "success"|"error", msg }
  const [confirmRun, setConfirmRun] = useState(null);  // routine id awaiting confirmation
  const [deletingRoutineId, setDeletingRoutineId] = useState(null);
  const [pageError, setPageError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const [rtData, skData] = await Promise.all([
        apiClient.get("/routines"),
        apiClient.get("/skills"),
      ]);
      setRoutines(rtData.items || []);
      setSkills(skData.items || []);
    } catch (err) {
      console.error("Failed to fetch routines:", err);
      setPageError("Routines could not be loaded. Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/routines/${id}`);
      setDeletingRoutineId(null);
      fetchData();
    } catch (err) {
      console.error("Delete failed:", err);
      setPageError("The routine could not be deleted.");
    }
  };

  const handleEdit = (item) => {
    setEditItem({ ...item });
    setShowForm(true);
    setDeletingRoutineId(null);
    setConfirmRun(null);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
    setDeletingRoutineId(null);
  };

  const handleRunRoutine = async (id) => {
    setConfirmRun(null);
    setRunStatus({ id, type: "running", msg: "Triggering…" });
    try {
      await apiClient.post(`/routines/${id}/run`);
      setRunStatus({ id, type: "success", msg: "Routine triggered!" });
      setTimeout(() => setRunStatus((s) => s?.id === id ? null : s), 4000);
    } catch (err) {
      console.error("Run failed:", err);
      setRunStatus({ id, type: "error", msg: "Failed to trigger routine." });
      setTimeout(() => setRunStatus((s) => s?.id === id ? null : s), 5000);
    }
  };

  return (
    <section className="routines-section">
      <div className="routines-intro">
        <h3>Scheduled Routines</h3>
        <p>Automated tasks Anna executes on a schedule through the routine worker.</p>
      </div>

      <div className="routines-stats">
        <span>{routines.length} routine{routines.length !== 1 ? "s" : ""}</span>
        <span className="routines-stat-separator">·</span>
        <span className="routines-active-count">
          {routines.filter((routine) => routine.active !== false).length} active
        </span>
        <div className="routines-stats-actions">
          <RefreshButton onClick={fetchData} title="Refresh routines" />
          {!showForm && (
            <button
              className="icon-btn icon-btn-add"
              onClick={handleAdd}
              title="New routine"
              aria-label="New routine"
            >
              +
            </button>
          )}
        </div>
      </div>

      {pageError && <div className="routines-error">{pageError}</div>}

      {showForm && !editItem && (
        <RoutineForm
          item={null}
          skills={skills}
          onSave={() => { setShowForm(false); fetchData(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="placeholder">Loading...</div>
      ) : (
        <div className="routines-list">
          {routines.length === 0 ? (
            <div className="placeholder">
              No routines yet. Create automated tasks that Anna executes on a schedule.
            </div>
          ) : (
            [...routines]
              .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
              .map((r) => (
                showForm && editItem?.id === r.id ? (
                  <RoutineForm
                    key={r.id}
                    item={editItem}
                    skills={skills}
                    onSave={() => { setShowForm(false); fetchData(); }}
                    onCancel={() => setShowForm(false)}
                  />
                ) : (
                  <article key={r.id} className={`routine-card ${r.active === false ? "paused" : ""}`}>
                    <div className="routine-card-header">
                      <div className="routine-title-group">
                        <h4>{r.name}</h4>
                        {r.think && <span className="routine-think-badge" title="Extended thinking">Think</span>}
                      </div>
                      <span className={`routine-status ${r.active !== false ? "active" : "inactive"}`}>
                          {r.active !== false ? "Active" : "Paused"}
                      </span>
                    </div>

                    <div className="routine-schedule">{scheduleLabel(r)}</div>

                    <div className="routine-metadata">
                      {r.maxExecutions > 0 && <span>{r.executionCount}/{r.maxExecutions} runs</span>}
                      {r.maxExecutions === 0 && r.executionCount > 0 && <span>{r.executionCount} runs</span>}
                      {r.nextRunAt && <span>Next: {new Date(r.nextRunAt).toLocaleString()}</span>}
                      {r.lastRunAt && <span>Last: {new Date(r.lastRunAt).toLocaleString()}</span>}
                    </div>

                    {(r.requiredSkills || []).length > 0 && (
                      <div className="routine-skills">
                        <span>Skills</span>
                        {(r.requiredSkills || []).map((skillId) => {
                          const skill = skills.find((candidate) => candidate.id === skillId);
                          return <span key={skillId} className="routine-skill-chip">{skill ? skill.name : skillId.slice(0, 8)}</span>;
                        })}
                      </div>
                    )}

                    {runStatus?.id === r.id && (
                      <div className={`routine-run-status ${runStatus.type}`}>{runStatus.msg}</div>
                    )}
                    {confirmRun === r.id && (
                      <div className="routine-confirm-bar">
                        <span>Run this routine now?</span>
                        <button className="btn-primary btn-xs" onClick={() => handleRunRoutine(r.id)}>Run now</button>
                        <button className="btn-secondary btn-xs" onClick={() => setConfirmRun(null)}>Cancel</button>
                      </div>
                    )}
                    {deletingRoutineId === r.id && (
                      <div className="routine-delete-bar">
                        <span>Delete this routine?</span>
                        <button className="btn-card-action btn-card-danger" onClick={() => handleDelete(r.id)}>Delete</button>
                        <button className="btn-card-action" onClick={() => setDeletingRoutineId(null)}>Cancel</button>
                      </div>
                    )}

                    <div className="routine-card-footer">
                      {r.createdAt && <span className="routine-date">Created {new Date(r.createdAt).toLocaleDateString()}</span>}
                      <div className="routine-actions">
                        <button
                          className="btn-card-action routine-run-action"
                          onClick={() => { setConfirmRun(r.id); setDeletingRoutineId(null); }}
                          disabled={runStatus?.id === r.id && runStatus.type === "running"}
                        >
                          Run
                        </button>
                        <button className="btn-card-action" onClick={() => handleEdit(r)}>Edit</button>
                        <button
                          className="btn-card-action btn-card-danger"
                          onClick={() => { setDeletingRoutineId(r.id); setConfirmRun(null); }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </article>
                )
              ))
          )}
        </div>
      )}
    </section>
  );
}

function RoutineForm({ item, skills = [], onSave, onCancel }) {
  const isEditing = Boolean(item);
  const formRef = useRef(null);
  const promptRef = useRef(null);
  const [form, setForm] = useState({
    name: item?.name || "",
    prompt: item?.prompt || "",
    frequency: item?.frequency || "daily",
    intervalMinutes: item?.intervalMinutes ?? 1440,
    executionTime: item?.executionTime || "09:00",
    repeatEveryDays: item?.repeatEveryDays ?? 1,
    executionDays: item?.executionDays || [],
    repeatEveryWeeks: item?.repeatEveryWeeks ?? 1,
    executionDayOfMonth: item?.executionDayOfMonth ?? 1,
    repeatEveryMonths: item?.repeatEveryMonths ?? 1,
    active: item?.active !== false,
    think: item?.think || false,
    maxExecutions: item?.maxExecutions ?? 0,
    requiredSkills: item?.requiredSkills || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const autoSize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  useEffect(() => {
    autoSize(promptRef.current);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const toggleDay = (day) => {
    setForm((prev) => {
      const days = prev.executionDays.includes(day)
        ? prev.executionDays.filter((d) => d !== day)
        : [...prev.executionDays, day];
      return { ...prev, executionDays: days };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        await apiClient.put(`/routines/${item.id}`, form);
      } else {
        await apiClient.post("/routines", form);
      }
      onSave();
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const showTimePicker = ["daily", "weekly", "monthly"].includes(form.frequency);
  const showDayPicker = form.frequency === "weekly";
  const showDomPicker = form.frequency === "monthly";
  const showInterval = ["minutes", "hourly"].includes(form.frequency);

  return (
    <div className="routine-form-card" ref={formRef}>
      <h3>{isEditing ? "Edit" : "Add"} Routine</h3>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Daily Agenda Summary"
            required
          />
        </div>
        <div className="form-group">
          <label>Prompt (what Anna should do)</label>
          <textarea
            ref={promptRef}
            value={form.prompt}
            onChange={(e) => {
              setForm({ ...form, prompt: e.target.value });
              autoSize(e.target);
            }}
            placeholder="e.g., Check my schedule for today and send me a summary of what's coming up, including any pending reminders."
            rows={1}
            className="routine-auto-textarea"
            required
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Frequency</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            >
              {FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {showTimePicker && (
            <div className="form-group">
              <label>Time</label>
              <input
                type="time"
                value={form.executionTime}
                onChange={(e) => setForm({ ...form, executionTime: e.target.value })}
              />
            </div>
          )}
          {showInterval && (
            <div className="form-group">
              <label>Interval</label>
              <select
                value={form.intervalMinutes}
                onChange={(e) => setForm({ ...form, intervalMinutes: parseInt(e.target.value) })}
              >
                {INTERVAL_PRESETS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {form.frequency === "daily" && (
          <div className="form-group">
            <label>Repeat every</label>
            <div className="cadence-input">
              <input
                type="number"
                min="1"
                value={form.repeatEveryDays}
                onChange={(e) => setForm({ ...form, repeatEveryDays: Math.max(1, parseInt(e.target.value) || 1) })}
              />
              <span>{form.repeatEveryDays === 1 ? "day" : "days"}</span>
            </div>
          </div>
        )}
        {showDayPicker && (
          <>
            <div className="form-group">
              <label>Days</label>
              <div className="day-picker">
                {DAY_OPTIONS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`day-btn ${form.executionDays.includes(day) ? "selected" : ""}`}
                    onClick={() => toggleDay(day)}
                  >
                    {dayLabel(day)}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Repeat every</label>
              <div className="cadence-input">
                <input
                  type="number"
                  min="1"
                  value={form.repeatEveryWeeks}
                  onChange={(e) => setForm({ ...form, repeatEveryWeeks: Math.max(1, parseInt(e.target.value) || 1) })}
                />
                <span>{form.repeatEveryWeeks === 1 ? "week" : "weeks"}</span>
              </div>
            </div>
          </>
        )}
        {showDomPicker && (
          <div className="form-row">
            <div className="form-group">
              <label>Day of Month (1-28)</label>
              <input
                type="number"
                min="1"
                max="28"
                value={form.executionDayOfMonth}
                onChange={(e) => setForm({ ...form, executionDayOfMonth: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="form-group">
              <label>Repeat every</label>
              <div className="cadence-input">
                <input
                  type="number"
                  min="1"
                  value={form.repeatEveryMonths}
                  onChange={(e) => setForm({ ...form, repeatEveryMonths: Math.max(1, parseInt(e.target.value) || 1) })}
                />
                <span>{form.repeatEveryMonths === 1 ? "month" : "months"}</span>
              </div>
            </div>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label>Max Runs (0 = unlimited)</label>
            <input
              type="number"
              min="0"
              value={form.maxExecutions}
              onChange={(e) => setForm({ ...form, maxExecutions: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <div className="toggle-group">
              <button type="button" className={`toggle-btn ${form.active ? "active" : ""}`} onClick={() => setForm({ ...form, active: true })}>Active</button>
              <button type="button" className={`toggle-btn ${!form.active ? "active" : ""}`} onClick={() => setForm({ ...form, active: false })}>Paused</button>
            </div>
          </div>
          <div className="form-group">
            <label>Thinking</label>
            <div className="toggle-group">
              <button type="button" className={`toggle-btn ${!form.think ? "active" : ""}`} onClick={() => setForm({ ...form, think: false })}>Fast</button>
              <button type="button" className={`toggle-btn think ${form.think ? "active" : ""}`} onClick={() => setForm({ ...form, think: true })}>🧠 Think</button>
            </div>
          </div>
        </div>
        {skills.length > 0 && (
          <div className="form-group">
            <label>Required Skills (loaded into context when this routine runs)</label>
            <div className="routine-option-picker">
              {skills.map((sk) => (
                <button
                  key={sk.id}
                  type="button"
                  className={`day-btn ${form.requiredSkills.includes(sk.id) ? "selected" : ""}`}
                  onClick={() => {
                    const ids = form.requiredSkills.includes(sk.id)
                      ? form.requiredSkills.filter((s) => s !== sk.id)
                      : [...form.requiredSkills, sk.id];
                    setForm({ ...form, requiredSkills: ids });
                  }}
                >
                  🧩 {sk.name}
                </button>
              ))}
            </div>
            {form.requiredSkills.length === 0 && (
              <p className="routine-form-help">
                No skills selected — all active skills will be loaded (default behavior).
              </p>
            )}
          </div>
        )}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Routines;
